import React, { useState } from 'react';
import { Mail, AlertCircle, ArrowRight, ChevronDown, CheckCircle } from 'lucide-react';
import { useAuth } from './context';
import { useTurnstile } from './turnstile';
import type { LoginFormProps } from './types';

/**
 * Shared login form — dark navy theme with orange accents and glassmorphism.
 *
 * 1. Microsoft SSO button (primary, prominent, white)
 * 2. "Backup login" toggle (collapsed by default)
 * 3. Email OTP / magic-link field with Turnstile captcha
 * 4. Domain restriction enforcement
 * 5. "Tilbage til cap4.dk" footer link
 */
export function LoginForm({
  appName,
  logoSrc,
  turnstileSiteKey,
  oauthRedirectUrl,
  backUrl = 'https://cap4.dk',
  backLabel = '\u2190 Tilbage til cap4.dk',
  domainRestriction = '@cap4.dk',
  oauthScopes = 'openid profile email',
}: LoginFormProps) {
  const { signIn, authError } = useAuth();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);

  const {
    token: captchaToken,
    reset: resetCaptcha,
    containerRef: turnstileRef,
  } = useTurnstile(turnstileSiteKey, showEmailLogin);

  // ── Handlers ────────────────────────────────────────────────────────────

  async function handleMicrosoftLogin() {
    setError('');
    setLoading(true);
    try {
      await signIn.withOAuth('azure', {
        scopes: oauthScopes,
        redirectTo: oauthRedirectUrl,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Kunne ikke logge ind med Microsoft';
      setError(message);
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setOtpSent(false);

    if (domainRestriction && !email.toLowerCase().endsWith(domainRestriction.toLowerCase())) {
      setError(`Kun ${domainRestriction} emails er tilladt`);
      return;
    }

    if (turnstileSiteKey && !captchaToken) {
      setError('Vent venligst p\u00e5 sikkerhedsverifikation');
      return;
    }

    setLoading(true);

    try {
      await signIn.withOtp(email, {
        redirectTo: oauthRedirectUrl,
        captchaToken: captchaToken || undefined,
      });
      // Always show success regardless of whether email exists
      setOtpSent(true);
    } catch {
      setError('Der opstod en fejl. Pr\u00f8v igen.');
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden"
      style={{ backgroundColor: '#07101a' }}
    >
      {/* Background decoration */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full blur-[80px] pointer-events-none"
        style={{ backgroundColor: 'rgba(241, 103, 61, 0.05)' }}
      />
      <div
        className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full blur-[100px] pointer-events-none"
        style={{ backgroundColor: 'rgba(0, 64, 95, 0.1)' }}
      />

      {/* Geometric accent lines */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[15%] left-[8%] w-px h-32 bg-gradient-to-b from-transparent via-white/10 to-transparent" />
        <div className="absolute top-[15%] left-[8%] w-32 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        <div className="absolute bottom-[15%] right-[8%] w-px h-32 bg-gradient-to-b from-transparent via-white/10 to-transparent" />
        <div className="absolute bottom-[15%] right-[8%] w-32 h-px bg-gradient-to-r from-white/10 via-white/10 to-transparent" />
      </div>

      <div className="relative z-10 w-full max-w-[400px]">
        {/* Logo */}
        <div className="text-center mb-10">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl border mb-5"
            style={{
              backgroundColor: 'rgba(241, 103, 61, 0.1)',
              borderColor: 'rgba(241, 103, 61, 0.2)',
              boxShadow: '0 0 20px rgba(241, 103, 61, 0.15)',
            }}
          >
            <img
              src={logoSrc}
              alt="CAP4Technologies"
              className="w-9 h-9 object-contain"
              width="36"
              height="36"
            />
          </div>
          <h1
            className="font-bold text-3xl tracking-tight"
            style={{ color: '#dce8f0' }}
          >
            CAP4
            <span
              style={{
                background: 'linear-gradient(135deg, #f1673d, #ff9a6c)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {appName}
            </span>
          </h1>
          <p className="text-sm mt-2" style={{ color: '#5e8099' }}>
            Log ind for at fortsætte
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-xl p-6 shadow-2xl"
          style={{
            backgroundColor: '#101f2e',
            border: '1px solid rgba(255, 255, 255, 0.09)',
          }}
        >
          {(error || authError) && (
            <div
              className="flex items-start gap-3 px-4 py-3 rounded-lg mb-5"
              style={{
                backgroundColor: 'rgba(248, 113, 113, 0.1)',
                border: '1px solid rgba(248, 113, 113, 0.25)',
              }}
            >
              <AlertCircle
                className="w-4 h-4 flex-shrink-0 mt-0.5"
                style={{ color: '#f87171' }}
              />
              <p className="text-sm" style={{ color: '#f87171' }}>
                {error || authError}
              </p>
            </div>
          )}

          {/* Microsoft SSO — primary login */}
          <button
            onClick={handleMicrosoftLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 py-3 px-4 rounded-lg font-semibold text-base hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 21 21"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            {loading && !showEmailLogin
              ? 'Opretter forbindelse...'
              : 'Log ind med Microsoft'}
          </button>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div
                className="w-full"
                style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
              />
            </div>
            <div className="relative flex justify-center">
              <button
                onClick={() => setShowEmailLogin(!showEmailLogin)}
                className="flex items-center gap-1 px-3 py-1 text-xs transition-colors hover:text-white/80"
                style={{ color: '#668ba6', backgroundColor: '#101f2e' }}
              >
                Backup login
                <ChevronDown
                  className={`w-3 h-3 transition-transform ${showEmailLogin ? 'rotate-180' : ''}`}
                />
              </button>
            </div>
          </div>

          {/* Backup login (collapsed by default) */}
          {showEmailLogin && (
            <>
              {otpSent ? (
                <div
                  className="flex items-start gap-3 px-4 py-3 rounded-lg"
                  style={{
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.25)',
                  }}
                >
                  <CheckCircle
                    className="w-4 h-4 flex-shrink-0 mt-0.5"
                    style={{ color: '#22c55e' }}
                  />
                  <p className="text-sm" style={{ color: '#22c55e' }}>
                    Vi har sendt et login-link til din email
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="cap4-auth-email"
                      className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                      style={{ color: '#5e8099' }}
                    >
                      Email
                    </label>
                    <div className="relative">
                      <Mail
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                        style={{ color: '#5e8099' }}
                      />
                      <input
                        id="cap4-auth-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full pl-9 pr-3.5 py-2.5 rounded-lg text-sm outline-none transition-all"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          color: '#dce8f0',
                        }}
                        placeholder={`mail${domainRestriction}`}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'rgba(241, 103, 61, 0.6)';
                          e.target.style.boxShadow =
                            '0 0 0 1px rgba(241, 103, 61, 0.3)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                  </div>

                  <div ref={turnstileRef as React.RefObject<HTMLDivElement>} className="flex justify-center" />

                  <button
                    type="submit"
                    disabled={loading || Boolean(turnstileSiteKey && !captchaToken)}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 text-white text-base font-semibold rounded-lg transition-all mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#f1673d' }}
                    onMouseEnter={(e) => {
                      if (!loading)
                        (e.target as HTMLButtonElement).style.backgroundColor =
                          '#e05530';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor =
                        '#f1673d';
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sender...
                      </>
                    ) : (
                      <>
                        Send login-link
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <a
            href={backUrl}
            className="text-xs transition-colors hover:text-white/80"
            style={{ color: '#5e8099' }}
          >
            {backLabel}
          </a>
        </div>
      </div>
    </div>
  );
}
